<template>
  <div class="favorites-container">
    <h2 class="page-title">我的收藏</h2>
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="posts.length === 0" class="no-data">
      <i class="el-icon-star-off"></i>
      <div>暂无收藏内容</div>
    </div>
    <div v-else class="posts-list">
      <div 
        v-for="post in posts" 
        :key="post.id" 
        class="post-card"
        @click="$emit('show-post-detail', post.id)"
      >
        <div class="post-header">
          <div class="post-author-left">
            <el-avatar 
              :size="40" 
              :src="post.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
              class="author-avatar"
            ></el-avatar>
            <div class="post-author-info">
              <h4>{{ post.name }}</h4>
              <span class="post-time">{{ formatDate(post.createTime) }}</span>
            </div>
          </div>
          <div class="header-actions">
            <!-- 只有当前用户自己的帖子才显示编辑和删除按钮 -->
            <template v-if="computedUserId === post.userId">
            </template>
            <el-button 
              v-else
              :type="post.isFollowed ? 'success' : 'default'"
              size="small"
              class="follow-btn"
              @click.stop="handleToggleFollow(post)"
            >
              {{ post.isFollowed ? '已关注' : '+ 关注' }}
            </el-button>
          </div>
        </div>
        <div class="post-content">
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-text">{{ post.content }}</p>
          <img v-if="post.coverImage" :src="post.coverImage" alt="cover" class="post-cover" />
        </div>
        <div class="post-stats">
          <span class="stat-item">
            <el-icon :size="14"><ThumbUpIcon /></el-icon>{{ post.likeCount }}
          </span>
          <span class="stat-item">
            <el-icon :size="14"><ChatLineRoundIcon /></el-icon>{{ post.commentCount }}
          </span>
          <span class="stat-item">
            <el-icon :size="14"><StarIcon /></el-icon>{{ post.favoriteCount }}
          </span>
          <span class="stat-item">
            <el-icon :size="14"><ShareIcon /></el-icon>{{ post.shareCount }}
          </span>
        </div>
        
        <!-- 操作按钮区域移到右下角 -->
        <div v-if="computedUserId === post.userId" class="post-actions">
          <el-button 
            size="small"
            type="primary"
            class="action-btn edit-btn"
            @click.stop="handleEditPost(post)"
          >
            编辑
          </el-button>
          <el-button 
            size="small"
            type="danger"
            class="action-btn delete-btn"
            @click.stop="handleDeletePost(post)"
          >
            删除
          </el-button>
        </div>
      </div>
    </div>
    
    <!-- 编辑帖子模态框 -->
    <EditPostModal 
      v-model="showEditModal"
      :post-id="currentEditPostId"
      @save-success="handleSaveSuccess"
      @close="handleModalClose"
    />
    
    <el-pagination
      v-if="total > pageSize"
      class="pagination"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="total"
      layout="prev, pager, next"
      @current-change="handlePageChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, defineEmits } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useStore } from 'vuex';
import { getUserFavorites } from '@/user/api/userApi';
import { toggleLikePost, toggleFavoritePost, followUser, deletePost as deletePostAPI } from '@/user/api/post';
import { formatDate } from '@/utils/date';
import { 
  ThumbUp as ThumbUpIcon, 
  ChatLineRound as ChatLineRoundIcon, 
  Star as StarIcon, 
  Share as ShareIcon 
} from '@element-plus/icons-vue';
import EditPostModal from './EditPostModal.vue'; // 导入编辑帖子模态框组件

const store = useStore(); // 获取store实例

// 定义组件可以触发的事件
defineEmits(['show-post-detail']);

interface Post {
  id: number;
  title: string;
  content: string;
  coverImage?: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  userId: number;       // 帖子发布者的ID
  name: string;         // 帖子发布者的姓名
  avatar: string;       // 帖子发布者的头像
  isFollowed: number;   // 是否已关注发布者 (0: 未关注, 1: 已关注)
  createTime?: string;  // 创建时间
}

const posts = ref<Post[]>([]);
const loading = ref(true);
const currentPage = ref(1);
const total = ref(0);
const pageSize = ref(5);

// 编辑相关状态
const showEditModal = ref(false);
const currentEditPostId = ref<number | null>(null);

// 计算属性获取当前登录用户的ID
const computedUserId = computed(() => {
  const userInfo = store.state.userInfo;
  return userInfo?.id || null;
});

// 处理编辑按钮点击
const handleEditPost = (post: Post) => {
  currentEditPostId.value = post.id;
  showEditModal.value = true;
};

// 处理删除按钮点击
const handleDeletePost = async (post: Post) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除帖子 "${post.title}" 吗？此操作不可撤销。`,
      '删除确认',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    const response = await deletePostAPI(post.id);
    if (response.code === 1) {
      ElMessage.success('删除成功');
      // 从列表中移除该帖子
      const index = posts.value.findIndex(p => p.id === post.id);
      if (index !== -1) {
        posts.value.splice(index, 1);
        total.value--;
      }
    } else {
      ElMessage.error(response.msg || '删除失败');
    }
  } catch (error) {
    console.error('删除帖子失败:', error);
    if (error !== 'cancel') { // 如果不是用户取消操作
      ElMessage.error('删除失败，请稍后重试');
    }
  }
};

// 处理保存成功
const handleSaveSuccess = () => {
  ElMessage.success('帖子更新成功');
  loadFavorites(); // 重新加载收藏列表
};

// 处理模态框关闭
const handleModalClose = () => {
  currentEditPostId.value = null;
};

// 处理关注/取消关注
const handleToggleFollow = async (post: Post) => {
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 调用API进行关注/取消关注
    const response = await followUser(post.userId);

    if (response && response.code === 1) {
      // 更新关注状态 - 反转当前状态
      post.isFollowed = !post.isFollowed;
      ElMessage.success(post.isFollowed ? '已关注' : '取消关注成功');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error) {
    console.error('操作失败:', error);
    ElMessage.error('操作失败，请稍后重试');
  }
};

// 获取收藏列表
const loadFavorites = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    const response = await getUserFavorites(params);
    console.log('收藏列表响应数据:', response); // 调试信息
    
    // 处理可能的数据结构差异
    let responseData;
    if (response.data) {
      // 如果响应在外层有一个data包装
      responseData = response.data;
    } else {
      // 如果响应直接就是数据
      responseData = response;
    }
    
    // 检查数据结构，兼容不同格式
    if (responseData && responseData.records) {
      // 标准格式: { records: [...], total: x }
      posts.value = responseData.records.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      }));
      total.value = responseData.total || 0;
    } else if (Array.isArray(responseData)) {
      // 直接数组格式: [...]
      posts.value = responseData.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      }));
      total.value = responseData.length;
    } else if (responseData && responseData.data && responseData.data.records) {
      // 双层包装格式: { data: { records: [...], total: x }}
      posts.value = responseData.data.records.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      })) || [];
      total.value = responseData.data.total || 0;
    } else {
      // 尝试其他可能的字段名
      const possibleRecordsKeys = ['records', 'data', 'list', 'items'];
      let foundRecords = false;
      
      for (const key of possibleRecordsKeys) {
        if (responseData[key]) {
          posts.value = Array.isArray(responseData[key]) ? responseData[key] : [];
          foundRecords = true;
          break;
        }
      }
      
      if (!foundRecords) {
        posts.value = [];
      }
      
      // 查找总数字段
      const possibleTotalKeys = ['total', 'totalCount', 'count'];
      for (const key of possibleTotalKeys) {
        if (responseData[key] !== undefined) {
          total.value = responseData[key] || 0;
          break;
        }
      }
    }
    
    console.log(`加载到 ${posts.value.length} 条收藏，总计 ${total.value}`); // 调试信息
    console.log('第一条收藏数据结构:', posts.value.length > 0 ? JSON.stringify(posts.value[0], null, 2) : '暂无数'); // 调试信息
  } catch (error) {
    console.error('获取收藏列表失败:', error);
    ElMessage.error('获取收藏列表失败');
  } finally {
    loading.value = false;
  }
};


// 分页改变
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadFavorites();
};

onMounted(() => {
  loadFavorites();
});
</script>

<style scoped>
.favorites-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  min-height: 500px;
}

.page-title {
  text-align: center;
  margin-bottom: 20px;
  color: #8B7D6B;
  font-size: 24px;
  font-weight: 600;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #a09585;
}

.no-data i {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
  color: #d4c19c;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .favorites-container {
    margin: 10px;
    padding: 15px;
  }
  
  .post-stats {
    gap: 12px;
    font-size: 13px;
  }
  
  .post-title {
    font-size: 17px;
  }
  
  .post-header {
    flex-direction: row;
    align-items: flex-start;
  }
}

.post-card {
  border: 1px solid #e9dcc8;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  position: relative;
  overflow: hidden;
}

.post-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(to right, #d48341, #e6ab6a);
  opacity: 0;
  transition: opacity 0.3s;
}

.post-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(212, 131, 65, 0.15);
  background: #fefefe;
}

.post-card:hover::before {
  opacity: 1;
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e9dcc8;
}

.post-author-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.author-avatar {
  border: 2px solid #e9dcc8;
}

.post-author-info h4 {
  margin: 0 0 4px 0;
  font-size: 15px;
  color: #8B7D6B;
  font-weight: bold;
}

.post-time {
  font-size: 12px;
  color: #a09585;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.follow-btn {
  margin-left: auto;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  background-color: #f8f3eb;
  color: #8b6d4b;
  border: 1px solid #e9dcc8;
}

.follow-btn:hover {
  background-color: #f0e6d4;
  color: #7a5e3a;
  border: 1px solid #d4a976;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.follow-btn.el-button--success {
  background-color: #e8f5e9;
  color: #4caf50;
  border: 1px solid #c8e6c9;
}

.follow-btn.el-button--success:hover {
  background-color: #c8e6c9;
  color: #388e3c;
  border: 1px solid #a5d6a7;
}

.post-content {
  margin-bottom: 15px;
}

.post-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #3c3c3c;
  line-height: 1.4;
}

.post-text {
  color: #666;
  line-height: 1.6;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-cover {
  width: 100%;
  aspect-ratio: 16 / 9; /* 设置宽高比为16:9 */
  border-radius: 8px;
  margin-top: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  object-fit: cover; /* 保持图片比例填充整个容器 */
  display: block;
}

.post-stats {
  display: flex;
  gap: 20px;
  color: #a09585;
  font-size: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e9dcc8;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-item .el-icon {
  color: #d48341;
}

.pagination {
  margin-top: 25px;
  text-align: center;
  padding: 20px 0;
}

/* 新增：右下角操作按钮样式 */
.post-actions {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

/* 保持按钮原有样式，确保与其他按钮样式一致 */
.post-actions .action-btn {
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.post-actions .edit-btn {
  background-color: #f0f9ff;
  color: #409eff;
  border: 1px solid #b3d8ff;
}

.post-actions .edit-btn:hover {
  background-color: #ecf5ff;
  color: #66b1ff;
  border: 1px solid #8cc5ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.2);
}

.post-actions .delete-btn {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}

.post-actions .delete-btn:hover {
  background-color: #fef7f7;
  color: #f78989;
  border: 1px solid #f89898;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(245, 108, 108, 0.2);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .favorites-container {
    margin: 10px;
    padding: 15px;
  }
  
  .post-stats {
    gap: 12px;
    font-size: 13px;
  }
  
  .post-title {
    font-size: 17px;
  }
  
  .post-actions {
    bottom: 10px;
    right: 10px;
  }
}
</style>